<template>
  <div class="flex flex-col lg:flex-row lg:space-x-6 mr-6 w-full">
    <div
      class="bg-white rounded-xl rounded-tl-none shadow w-full py-16 px-12 lg:p-16 mb-12 lg:mb-0 h-full"
    >
      <div class="flex flex-wrap lg:flex-nowrap relative">
        <!-- Col 2  - Newsletter stats/history' -->
        <div
          class="lg:w-1/2 2xl:w-1/3 w-full flex-shrink-0 py-8 pr-16 relative"
        >
          <!-- Previous Newsletters -->
          <div>
            <h3 class="mb-4 border-b pb-6">Previous Newsletters</h3>
            <div v-if="newslettersLoading" class="w-full h-14">
              <lottie :options="loadingAnimationOptions" />
            </div>
            <div v-else-if="!!previousNewsletters.length">
              <div class="w-full mr-6">
                <div
                  v-for="(option, i) in previousNewsletters"
                  :key="i"
                  class="relative hover:bg-truegray-100 flex items-center w-full transition-all duration-100 group py-2 group border-truegray-100 border-b"
                >
                  <div
                    class="w-1/12 flex items-center justify-center seperator-right relative"
                  >
                    <h6
                      class="group-hover:opactiy-0 pointer-events-none transition-all"
                    >
                      #{{ option.id }}
                    </h6>
                  </div>
                  <div class="w-6/12 pl-6 pb-2 pr-4">
                    <p
                      :style="{ fontSize: '12px' }"
                      class="newsletter-post-title transition-all"
                      :class="
                        option.status === 'Draft'
                          ? ''
                          : 'cursor-pointer hover:text-primary'
                      "
                      @click="copyLink(option, true)"
                    >
                      {{ option.title }}
                    </p>
                    <p v-if="option.subject" class="newsletter-body-text">
                      {{ option.subject }}
                    </p>
                    <p
                      v-if="option.status === 'Processing'"
                      class="text-truegray-500 italic no-underline text-xs"
                    >
                      Stats will become available after processing. Check back
                      soon...
                    </p>
                    <div class="flex space-x-3 mt-2">
                      <div
                        v-if="option.status === 'Sent'"
                        title="View stats"
                        class="text-truegray-300 hover:text-primary transition-all cursor-pointer"
                        @click="openStatsModal(option)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4"
                        >
                          <path
                            d="M12 9a1 1 0 01-1-1V3c0-.553.45-1.008.997-.93a7.004 7.004 0 015.933 5.933c.078.547-.378.997-.93.997h-5z"
                          />
                          <path
                            d="M8.003 4.07C8.55 3.992 9 4.447 9 5v5a1 1 0 001 1h5c.552 0 1.008.45.93.997A7.001 7.001 0 012 11a7.002 7.002 0 016.003-6.93z"
                          />
                        </svg>
                      </div>

                      <div
                        v-if="option.status === 'Draft'"
                        title="Edit draft"
                        class="text-truegray-300 hover:text-primary transition-all cursor-pointer"
                        @click="editDraft(option)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4"
                        >
                          <path
                            d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"
                          />
                        </svg>
                      </div>

                      <div
                        v-if="option.status !== 'Draft'"
                        title="Copy link"
                        class="text-truegray-300 hover:text-primary transition-all cursor-pointer"
                        @click="copyLink(option)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          class="w-4 h-4"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M15.988 3.012A2.25 2.25 0 0118 5.25v6.5A2.25 2.25 0 0115.75 14H13.5V7A2.5 2.5 0 0011 4.5H8.128a2.252 2.252 0 011.884-1.488A2.25 2.25 0 0112.25 1h1.5a2.25 2.25 0 012.238 2.012zM11.5 3.25a.75.75 0 01.75-.75h1.5a.75.75 0 01.75.75v.25h-3v-.25z"
                            clip-rule="evenodd"
                          />
                          <path
                            fill-rule="evenodd"
                            d="M2 7a1 1 0 011-1h8a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V7zm2 3.25a.75.75 0 01.75-.75h4.5a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75zm0 3.5a.75.75 0 01.75-.75h4.5a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <!-- <p v-if="option.status === 'Draft' || option.status === 'Scheduled'" class="text-primary underline text-xs cursor-pointer">Edit Newsletter</p> -->
                    </div>
                  </div>
                  <div class="w-5/12 flex items-center justify-between">
                    <div
                      :class="[
                        option.status === 'Sent' ? 'bg-green-400' : '', // Emailed
                        option.status === 'Processing' ? 'bg-cw-alt' : '', // tailwind yellows not working beyond 300... tbd fix
                        option.status === 'Scheduled' ? 'bg-green-500' : '', // Scheduled to send in future
                        option.status === 'Published' ? 'bg-primary' : '', // Saved for link sharing, not emailed
                        option.status === 'Draft' ? 'bg-truegray-500' : '', // Draft
                        option.status === 'Error' ? 'bg-red-400' : '', // Error. No hail id but sent_at
                      ]"
                      class="rounded-xl px-3 py-1 text-white text-xs overflow-hidden"
                    >
                      {{ option.status }}
                    </div>
                    <div class="">
                      <p
                        v-if="option.sent_at && option.status === 'Sent'"
                        class="text-truegray-400 text-xs italic pr-2"
                      >
                        {{ option.sent_at_ui }}
                      </p>
                    </div>
                  </div>
                </div>
                <!--
                  <div
                  v-for="(option, i) in previousNewsletters"
                  :key="i"
                  class="relative flex w-full transition-all duration-100 group mb-2 py-2 group"
                  >
                  <h6
                  class="w-2/12 flex items-center justify-center seperator-right relative"
                  >
                  <span>#{{ option.number }}</span>
                  </h6>
                  <p
                  class="pl-6 text-truegray-500 flex items-center"
                  >
                  {{ option.subject }}
                  </p> 
                -->
              </div>
              <!-- PAGINATION -->
              <div
                v-if="pagination.previouspage || pagination.nextpage"
                class="flex justify-between items-center h-10"
              >
                <span
                  :disabled="!pagination.previouspage"
                  :class="[
                    !pagination.previouspage
                      ? 'opacity-25 cursor-not-allowed'
                      : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                  ]"
                  class="text-gray-500"
                  @click="
                    getPreviousNewsletters((gotopage = pagination.previouspage))
                  "
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 17l-5-5m0 0l5-5m-5 5h12"
                    />
                  </svg>
                </span>
                <span class="text-center text-xs"
                  >Page {{ pagination.current_page }} of
                  {{ pagination.total_pages }}</span
                >
                <span
                  :disabled="!pagination.nextpage"
                  :class="[
                    !pagination.nextpage
                      ? 'opacity-25 cursor-not-allowed'
                      : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                  ]"
                  class="text-gray-500"
                  @click="
                    getPreviousNewsletters((gotopage = pagination.nextpage))
                  "
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </div>
            </div>
            <div v-else>
              <p class="text-sm text-truegray-400">
                No previous newsletters found.
              </p>
            </div>
          </div>
        </div>

        <div class="lg:w-1/2 2xl:w-2/3 w-full py-8 2xl:pl-12 flex-shrink-0">
          <div class="flex flex-row mb-2">
            <h2 class="mr-6 capitalize">Newsletter Builder</h2>
            <button
              title="Reset newsletter builder"
              class="transform hover:rotate-180 transition-all duration-300 h-full mb-3 self-center"
              @click="resetNewsletterFields"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 hover:text-primary"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
            </button>
          </div>

          <div
            v-if="autoNewsletterStatus?.title && !autoNewsletterStatusLoading"
            class="rounded-xl mb-8"
          >
            <div
              class="bg-green-400 px-6 py-2 font-bold text-sm flex justify-between cursor-pointer"
              :class="autoNewsletterDetailsOpen ? 'rounded-t-xl' : 'rounded-xl'"
              @click="toggleNewsletterStatusDD"
            >
              Automatic Newsletter Enabled
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5 transition-all"
                :class="autoNewsletterDetailsOpen ? 'rotate-180' : ''"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </div>
            <div
              v-if="autoNewsletterDetailsOpen"
              class="p-4 border border-truegray-300 rounded-b-xl"
            >
              <p class="text-truegray-500 text-sm">
                Title:
                <span class="text-black text-base ml-2">{{
                  autoNewsletterStatus.title
                }}</span>
              </p>
              <p class="text-truegray-500 text-sm">
                Subject:
                <span class="text-black text-base ml-2">{{
                  autoNewsletterStatus.subject
                }}</span>
              </p>
              <p class="text-truegray-500 text-sm">
                Send Day:
                <span class="text-black text-base ml-2">{{
                  autoNewsletterStatus.day
                }}</span>
              </p>
              <p class="text-truegray-500 text-sm">
                Send Time:
                <span class="text-black text-base ml-2">{{
                  autoNewsletterStatus.time
                }}</span>
              </p>

              <p
                v-if="autoNewsletterMailLists"
                class="text-truegray-500 text-sm"
              >
                Send To:
                <span class="text-black text-base ml-2">{{
                  autoNewsletterMailLists
                }}</span>
              </p>

              <p class="text-truegray-500 text-sm">
                Last Sent:
                <span
                  v-if="autoNewsletterStatus.lastSent"
                  class="text-black text-base ml-2"
                  >{{ autoNewsletterStatus.lastSent }}</span
                >
                <span v-else class="text-black text-base ml-2">Never</span>
              </p>
            </div>
            <p
              class="text-sm mt-2 text-red-400 w-full text-right cursor-pointer hover:text-red-600"
              @click="dispatchTurnOffAutoNewsletter"
            >
              Turn off Automatic Newsletter
            </p>
          </div>

          <div v-if="autoNewsletterStatusLoading" class="w-full h-14">
            <lottie :options="loadingAnimationOptions" />
          </div>

          <NewsletterBuilder :config="{ build: true }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { mapActions, mapGetters } from 'vuex'
import NewsletterBuilder from './NewsletterBuilder.vue'
import * as loadingAnimation from '../../../../../shared/components/loading-dark.json'

export default {
  components: {
    NewsletterBuilder,
  },

  data() {
    return {
      loadingAnimationOptions: {
        animationData: loadingAnimation.default,
      },

      loadingAnimate: false,

      // posts: [], // This is the list of post that will be assigned to the next newsletter
      // newsletterPosts: [], // Store an easily searchable list of current post ids that are in posts

      postsPerNewsletter: 30,

      newslettersLoading: false,
      previousNewsletters: [],

      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 6,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
        total: 0,
      },

      autoNewsletterStatusLoading: false,
      autoNewsletterDetailsOpen: false,

      toggleClass:
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
      inputWrapperClass:
        'rounded relative bg-white border border-truegray-300 w-full',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 m-0 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-24 font-primary text-black focus:placeholder-truegray-300 transition-all duration-100 ease-in-out w-full',
      ddButtonClass:
        'w-full flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-12 font-primary text-black cursor-pointer',
      ddSvgClass: 'cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2',
      ddSpanClass: 'block text-base font-primary truncate text-left mt-2',
      ddUlClass:
        'no-scrollbar transform ease-in-out duration-300 max-h-56 px-6 w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl',
      ddLiClass:
        'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
      imgWrapperClass:
        'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center sm relative z-20',
      imgButtonClass:
        'z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6',
      infoClass:
        'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none group-hover:opacity-100 bg-white',
    }
  },

  computed: {
    ...mapGetters({
      mailLists: 'careerwise/getMailLists',
      autoNewsletterStatus: 'careerwise/getAutoNewsletterStatus',
      getMailListsByHailIds: 'careerwise/getMailListsByHailIds',
    }),

    autoNewsletterMailLists() {
      const mailLists = JSON.parse(this.autoNewsletterStatus?.mailLists ?? '[]')

      const listsSentTo = this.getMailListsByHailIds(mailLists)
      const names = listsSentTo.map((list) => list.name)
      return names.join(', ')
    },
  },

  async mounted() {
    await this.getMailLists()
    this.getPreviousNewsletters()
    this.getAutoNewsletterStatus()

    EventBus.$on('refreshPreviousNewsletters', () => {
      this.getPreviousNewsletters()
    })

    EventBus.$on('autoNewsletterStatusChanged', () => {
      this.getAutoNewsletterStatus()
    })
  },

  methods: {
    ...mapActions({
      dispatchGetNewsletterPosts: 'careerwise/dispatchGetNewsletterPosts',
      dispatchGetNewsletters: 'careerwise/dispatchGetNewsletters',
      dispatchGetMailLists: 'careerwise/dispatchGetMailLists',
      dispatchGetAutoNewsletterStatus:
        'careerwise/dispatchGetAutoNewsletterStatus',
      dispatchTurnOffAutoNewsletter: 'careerwise/dispatchTurnOffAutoNewsletter',
    }),

    async getMailLists() {
      this.dispatchGetMailLists()
    },

    async getAutoNewsletterStatus() {
      this.$set(this, 'autoNewsletterStatusLoading', true)
      await this.dispatchGetAutoNewsletterStatus()
      this.$set(this, 'autoNewsletterStatusLoading', false)
    },

    resetNewsletterFields() {
      EventBus.$emit('resetNewsletterFields')
    },

    async getPreviousNewsletters(gotopage = null) {
      this.$set(this, 'newslettersLoading', true)

      const payload = {
        page: gotopage,
      }

      const response = await this.dispatchGetNewsletters(payload)
      if (response) {
        const newslettersData = response.data.newsletters

        this.$set(this, 'previousNewsletters', newslettersData.data)
        let total_pages =
          Math.floor(newslettersData.total / newslettersData.per_page) + 1
        this.$set(this.pagination, 'current_page', newslettersData.current_page)
        this.$set(this.pagination, 'total_pages', total_pages)
        this.$set(this.pagination, 'per_page', newslettersData.per_page)
        this.$set(
          this.pagination,
          'last_page_url',
          newslettersData.last_page_url,
        )
        this.$set(
          this.pagination,
          'next_page_url',
          newslettersData.next_page_url,
        )
        this.$set(
          this.pagination,
          'prev_page_url',
          newslettersData.prev_page_url,
        )
        this.$set(this.pagination, 'path', newslettersData.next_page_path)

        let prev = null
        if (this.pagination.prev_page_url) {
          prev = this.pagination.prev_page_url.split('=')
          prev = prev[1]
        }

        let next = null
        if (this.pagination.next_page_url) {
          next = this.pagination.next_page_url.split('=')
          next = next[1]
        }

        this.$set(this.pagination, 'previouspage', prev)
        this.$set(this.pagination, 'nextpage', next)
        this.$set(this, 'newslettersLoading', false)
      } else {
        this.$set(this, 'newslettersLoading', false)
        this.$set(this, 'previousNewsletters', [])
      }
    },

    openStatsModal(newsletter) {
      EventBus.$emit('showModal', {
        action: 'cw-newsletter-stats',
        data: {
          newsletter: newsletter,
        },
      })
    },

    showAutoNewsletterModal() {
      EventBus.$emit('showModal', 'autoNewsletterModal')
    },

    toggleNewsletterStatusDD() {
      this.$set(
        this,
        'autoNewsletterDetailsOpen',
        !this.autoNewsletterDetailsOpen,
      )
    },

    editDraft(newsletter) {
      EventBus.$emit('editDraftNewsletter', newsletter)
      let alert = {
        status: 'success',
        message: 'Draft loaded successfully',
      }
      EventBus.$emit('triggerAlertResponse', alert)
    },

    copyLink(newsletter, openInNewTab = false) {
      if (newsletter.status === 'Draft') {
        return
      }
      let url = window.location.origin + '/newsletter/' + newsletter.id
      let alert = {
        status: 'success',
        message: 'Link copied to clipboard',
      }
      if (openInNewTab) {
        const newTab = window.open(url, '_blank')
        newTab.focus()
        alert.message = 'Link opened in new tab'
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'absolute'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      EventBus.$emit('triggerAlertResponse', alert)
    },
  },
}
</script>

<style scoped>
.clamp-subject {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.seperator-right::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  height: 14px;
  width: 1px;
  transform: translateY(-50%);
  background: #e5e7eb;
}

.newsletter-preview * {
  font-family: 'Helvetica';
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid .col-span-2 {
  grid-column: span 2 / span 2 !important;
}

.newsletter-title {
  font-size: 30px;
  font-weight: 600;
}

.newsletter-body-text {
  font-size: 12px;
  color: #656d78;
  margin: 0;
}

.newsletter-post-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.clamp-subject {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.seperator-right::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  height: 14px;
  width: 1px;
  transform: translateY(-50%);
  background: #e5e7eb;
}
</style>
